import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getMyInfo } from '@/api/user'
import { getModules, getRoleModulePermissions } from '@/api/permission'

export const useAppStore = defineStore('app', () => {
  // 状态
  const activeSection = ref('report')
  const activeTab = ref('teaching-project')
  const todoTab = ref('my-reviews')
  const sidebarOpen = ref(false)
  const pendingFeedbackCount = ref(0)
  const userInfo = ref(null)
  const userRoleKey = ref('')
  const roleId = ref(null)
  const moduleNameToId = ref({})
  const allowedModules = ref([])
  const deniedModules = ref([])
  const functionPermissions = ref([])
  const permissionsLoaded = ref(false)
  const lastToken = ref('')

  // 方法
  const setActiveSection = (section) => {
    activeSection.value = section
  }

  const setActiveTab = (tab) => {
    activeTab.value = tab
  }

  const setTodoTab = (tab) => {
    todoTab.value = tab
  }

  const setSidebarOpen = (open) => {
    sidebarOpen.value = open
  }

  const toggleSidebar = () => {
    sidebarOpen.value = !sidebarOpen.value
  }

  const setPendingFeedbackCount = (count) => {
    const n = Number(count)
    pendingFeedbackCount.value = Number.isFinite(n) && n >= 0 ? n : 0
  }

  const loadPermissions = async () => {
    try {
      const info = await getMyInfo()
      const data = info?.data ?? info
      userInfo.value = data || null
      const rid = data?.roleId ?? data?.role_id ?? data?.roleID ?? data?.role?.id ?? null
      roleId.value = rid ?? null
      const ridNum = Number(roleId.value)
      const roleMap = { 1: 'teacher', 2: 'secretary', 3: 'director', 4: 'admin' }
      userRoleKey.value = roleMap[ridNum] || ''
      if (!userRoleKey.value) {
        allowedModules.value = []
        deniedModules.value = []
        permissionsLoaded.value = true
        return
      }
      const modulesRes = await getModules()
      const moduleList = modulesRes?.data ?? modulesRes ?? []
      const nameToId = {}
      const idToParent = {}
      const idToCode = {}
      const codeToId = {}
      moduleList.forEach(m => {
        const n = String(m.moduleName || '')
        const c = String(m.moduleCode || '')
        if (n) nameToId[n] = String(m.id)
        if (c) {
          nameToId[c] = String(m.id)
          codeToId[c] = String(m.id)
        }
        const idStr = String(m.id)
        const pid = Number(m.parentId || 0)
        idToParent[idStr] = String(pid || '')
        idToCode[idStr] = c
      })
      moduleNameToId.value = nameToId
      let perms = []
      if (roleId.value) {
        const permsRes = await getRoleModulePermissions(roleId.value)
        perms = permsRes?.data ?? permsRes ?? []
      }
      const allowedSet = new Set()
      const deniedSet = new Set()
      ;(Array.isArray(perms) ? perms : []).forEach(p => {
        const hv = p.hasPerm ?? p.hasPermission ?? p.permission ?? p.has_module ?? p.allow ?? p.enabled ?? p.has
        const v = typeof hv === 'string' ? hv.toLowerCase() : hv
        const ok = v === 1 || v === '1' || v === true || v === 'true' || v === 'yes' || v === 'y'
        const mid = String(p.moduleId ?? p.module_id ?? p.moduleID ?? p.module)
        if (ok) {
          allowedSet.add(mid)
        } else {
          deniedSet.add(mid)
        }
      })
      allowedModules.value = Array.from(allowedSet)
      deniedModules.value = Array.from(deniedSet)
    } catch {
      allowedModules.value = []
      deniedModules.value = []
    } finally {
      permissionsLoaded.value = true
    }
  }

  const defaultFunctionPermissions = [
    { id: 'achievements-view', moduleId: 'achievements', label: '查看成果列表', teacher: true, secretary: true, director: true, admin: true },
    { id: 'achievements-edit-submitted', moduleId: 'achievements', label: '编辑已提交成果', teacher: false, secretary: true, director: true, admin: true },
    { id: 'achievements-delete-submitted', moduleId: 'achievements', label: '删除已提交成果', teacher: false, secretary: true, director: true, admin: true },
    { id: 'profile-report-error', moduleId: 'profile', label: '申报信息有误', teacher: true, secretary: true, director: true, admin: true },
    { id: 'dept-view', moduleId: 'department-management', label: '查看教研室成果', teacher: false, secretary: false, director: true, admin: true },
    { id: 'dept-edit', moduleId: 'department-management', label: '编辑教研室成果', teacher: false, secretary: false, director: true, admin: true },
    { id: 'dept-delete', moduleId: 'department-management', label: '删除教研室成果', teacher: false, secretary: false, director: true, admin: true },
    { id: 'status-view', moduleId: 'teacher-status', label: '查看教师状态', teacher: false, secretary: true, director: false, admin: true },
    { id: 'status-edit', moduleId: 'teacher-status', label: '修改教师状态', teacher: false, secretary: true, director: false, admin: true },
    { id: 'overview-view', moduleId: 'info-overview', label: '查看全部数据', teacher: false, secretary: false, director: false, admin: true },
    { id: 'overview-export', moduleId: 'info-overview', label: '导出全部数据', teacher: false, secretary: false, director: false, admin: true }
  ]

  const loadFunctionPermissions = () => {
    try {
      const raw = localStorage.getItem('functionPermissions')
      const list = raw ? JSON.parse(raw) : defaultFunctionPermissions
      functionPermissions.value = Array.isArray(list) ? list : defaultFunctionPermissions
    } catch {
      functionPermissions.value = defaultFunctionPermissions
    }
  }

  const hasModuleByName = (name) => {
    const admin = userRoleKey.value === 'admin'
    const norm = (s) => String(s || '').toLowerCase().replace(/[\s_-]+/g, '')
    const raw = String(name || '')
    const aliases = {
      '填报信息': ['reportforms', 'report-forms', 'REPORT_FORMS', 'REPORT_INFO', '填报', 'REPORTFORMS'],
      '我的成果': ['achievements', 'ACHIEVEMENTS', '成果', '成果管理', 'MY_ACHIEVEMENT', 'MY-ACHIEVEMENT', 'MYACHIEVEMENT', 'ACHIEVEMENT'],
      '个人信息': ['profile', 'PROFILE', '个人', '信息', 'PERSONAL_INFO', 'PERSONAL-INFO', 'PERSONALINFO'],
      '教研室管理': ['departmentmanagement', 'department-management', 'DEPARTMENT_MANAGEMENT', 'DEPT_MANAGE', 'DEPT-MANAGE', 'DEPTMANAGE', '教研室', '部门管理'],
      '教师状态维护': ['teacherstatus', 'teacher-status', 'TEACHER_STATUS', 'TEACHER-STATUS', 'TEACHERSTATUS', '教师状态', '状态维护'],
      '信息总览': ['infooverview', 'info-overview', 'INFO_OVERVIEW', 'INFO-OVERVIEW', 'INFOOVERVIEW', '总览', '数据总览']
    }
    const candidates = [raw, ...(aliases[raw] || [])]
    let id = null
    for (const c of candidates) {
      const direct = moduleNameToId.value[c]
      if (direct) { id = direct; break }
    }
    if (!id) {
      const target = norm(raw)
      for (const [key, val] of Object.entries(moduleNameToId.value)) {
        const k = norm(key)
        if (k === target || k.includes(target) || target.includes(k)) { id = val; break }
      }
    }
    if (!id) return admin
    if (deniedModules.value.includes(String(id))) return admin
    return allowedModules.value.includes(String(id)) || admin
  }

  const hasFunctionByName = (name) => {
    const admin = userRoleKey.value === 'admin'
    const alias = {
      '查看教室状态': '查看教师状态',
      '修改教室状态': '修改教师状态',
      '全部导出数据': '导出全部数据'
    }
    const k = alias[name] || name
    const labelSynonyms = {
      '查看成果列表': ['查看成果', '查看我的成果', '成果列表', 'ACHIEVEMENTS_VIEW', 'MY_ACHIEVEMENT_VIEW', 'MY-ACHIEVEMENT-VIEW'],
      '编辑已提交成果': ['编辑成果', '编辑提交成果', 'ACHIEVEMENTS_EDIT_SUBMITTED', 'MY_ACHIEVEMENT_EDIT_SUBMITTED'],
      '删除已提交成果': ['删除成果', '删除提交成果', 'ACHIEVEMENTS_DELETE_SUBMITTED', 'MY_ACHIEVEMENT_DELETE_SUBMITTED'],
      '申报信息有误': ['信息有误', '申报信息错误', 'PROFILE_REPORT_ERROR', 'PERSONAL_INFO_REPORT_ERROR'],
      '查看教研室成果': ['查看教研室', 'DEPT_ACHIEVEMENT_VIEW', 'DEPT-ACHIEVEMENT-VIEW'],
      '编辑教研室成果': ['编辑教研室', 'DEPT_ACHIEVEMENT_EDIT', 'DEPT-ACHIEVEMENT-EDIT'],
      '删除教研室成果': ['删除教研室', 'DEPT_ACHIEVEMENT_DELETE', 'DEPT-ACHIEVEMENT-DELETE'],
      '查看教师状态': ['查看教师', 'TEACHER_STATUS_VIEW', 'TEACHER-STATUS-VIEW'],
      '修改教师状态': ['修改教师', 'TEACHER_STATUS_EDIT', 'TEACHER-STATUS-EDIT'],
      '查看全部数据': ['查看数据', 'INFO_OVERVIEW_VIEW', 'INFO-OVERVIEW-VIEW'],
      '导出全部数据': ['导出数据', 'INFO_OVERVIEW_EXPORT', 'INFO-OVERVIEW-EXPORT']
    }
    const candidates = [k, ...(labelSynonyms[k] || [])]
    const norm = (s) => String(s || '').toLowerCase().replace(/[\s_-]+/g, '')
    let id = null
    for (const c of candidates) {
      const direct = moduleNameToId.value[c]
      if (direct) { id = direct; break }
    }
    if (!id) {
      const target = norm(k)
      for (const [key, val] of Object.entries(moduleNameToId.value)) {
        const nk = norm(key)
        if (nk === target || nk.includes(target) || target.includes(nk)) { id = val; break }
      }
    }
    if (id) {
      if (deniedModules.value.includes(String(id))) return admin
      return allowedModules.value.includes(String(id)) || admin
    }
    const funcToModule = {
      '查看成果列表': '我的成果',
      '编辑已提交成果': '我的成果',
      '删除已提交成果': '我的成果',
      '申报信息有误': '个人信息',
      '查看教研室成果': '教研室管理',
      '编辑教研室成果': '教研室管理',
      '删除教研室成果': '教研室管理',
      '查看教师状态': '教师状态维护',
      '修改教师状态': '教师状态维护',
      '查看全部数据': '信息总览',
      '导出全部数据': '信息总览'
    }
    const moduleName = funcToModule[k] || ''
    if (!moduleName) return admin
    return hasModuleByName(moduleName)
  }

  const isAdmin = () => userRoleKey.value === 'admin'

  const ensurePermissionsSynced = async (token) => {
    const t = String(token || '')
    if (!permissionsLoaded.value || lastToken.value !== t) {
      await loadPermissions()
      loadFunctionPermissions()
      lastToken.value = t
    }
  }

  return {
    activeSection,
    activeTab,
    todoTab,
    sidebarOpen,
    pendingFeedbackCount,
    userInfo,
    userRoleKey,
    roleId,
    moduleNameToId,
    allowedModules,
    deniedModules,
    functionPermissions,
    permissionsLoaded,
    setActiveSection,
    setActiveTab,
    setTodoTab,
    setSidebarOpen,
    toggleSidebar,
    setPendingFeedbackCount,
    loadPermissions,
    loadFunctionPermissions,
    hasModuleByName,
    hasFunctionByName,
    isAdmin,
    ensurePermissionsSynced
  }
})
